import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 2rem 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-color);
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  padding: 0;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: #444;
  margin-bottom: 0.75rem;
  font-weight: 600;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.35rem;
  color: #444;
  margin-top: 0.5rem;
  margin-bottom: 0.3rem;
  font-weight: 600;
`;

const Content = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  
  p {
    margin-bottom: 0.5rem;
  }
  
  ul, ol {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.25rem;
    }
  }
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  
  th, td {
    border: 1px solid #ddd;
    padding: 0.75rem;
    text-align: left;
  }
  
  th {
    background-color: #f5f5f5;
    font-weight: 600;
  }
  
  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
`;

const LastUpdated = styled.p`
  font-style: italic;
  color: #777;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const CookiesPage: React.FC = () => {
  return (
    <PageContainer className="font--jakarta">
      <SEO 
        title="Cookie Policy"
        description="Learn about UPTREND Systems' Cookie Policy and how we use cookies and similar technologies on our website to enhance your browsing experience."
        keywords="cookie policy, website cookies, analytics cookies, UPTREND Systems cookies, web tracking, cookie consent, browser cookies"
        canonicalUrl="/cookies"
        noIndex={false}
        pageType="legal"
      />
      <PageTitle>Cookie Policy</PageTitle>
      
      <Section>
        <Content>
          <p>This Cookie Policy explains how Uptrend Systems ("we", "us", or "our") uses cookies and similar technologies to recognize you when you visit our website at <a href="https://uptrendsystems.com">uptrendsystems.com</a> ("Website"). It explains what these technologies are and why we use them, as well as your rights to control our use of them.</p>
          <p>Please read this Cookie Policy carefully. By continuing to browse or use our Website, you agree to our use of cookies as described in this Cookie Policy.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>1. What are cookies?</SectionTitle>
        <Content>
          <p>Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.</p>
          <p>Cookies set by the website owner (in this case, Uptrend Systems) are called "first-party cookies". Cookies set by parties other than the website owner are called "third-party cookies". Third-party cookies enable third-party features or functionality to be provided on or through the website (e.g., advertising, interactive content, and analytics). The parties that set these third-party cookies can recognize your computer both when it visits the website in question and also when it visits certain other websites.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>2. Why do we use cookies?</SectionTitle>
        <Content>
          <p>We use first and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Website. Third parties serve cookies through our Website for analytics and other purposes.</p>
          <p>The specific types of first and third-party cookies served through our Website and the purposes they perform are described below:</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>3. Types of cookies we use</SectionTitle>
        <Content>
          <SubSectionTitle>Essential Cookies</SubSectionTitle>
          <p>These cookies are strictly necessary to provide you with services available through our Website and to use some of its features, such as access to secure areas. Because these cookies are strictly necessary to deliver the Website, you cannot refuse them without impacting how our Website functions.</p>
          
          <SubSectionTitle>Analytics Cookies</SubSectionTitle>
          <p>These cookies help us understand how visitors interact with our Website. They provide us with information about areas of our Website visited, the time spent on our Website, and any issues encountered, such as error messages. These cookies help us improve the performance of our Website.</p>
          <p>We primarily use Google Analytics for this purpose. Google Analytics uses its own cookies. These cookies don't collect information that identifies a visitor. All information these cookies collect is aggregated and therefore anonymous. It is only used to improve how the Website works.</p>
          
          <Table>
            <thead>
              <tr>
                <th>Cookie</th>
                <th>Type</th>
                <th>Duration</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>_ga</td>
                <td>Analytics</td>
                <td>2 years</td>
                <td>Used by Google Analytics to distinguish users</td>
              </tr>
              <tr>
                <td>_gid</td>
                <td>Analytics</td>
                <td>24 hours</td>
                <td>Used by Google Analytics to distinguish users</td>
              </tr>
              <tr>
                <td>_gat</td>
                <td>Analytics</td>
                <td>1 minute</td>
                <td>Used by Google Analytics to throttle request rate</td>
              </tr>
            </tbody>
          </Table>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>4. How can you control cookies?</SectionTitle>
        <Content>
          <p>You can set or amend your web browser controls to accept or refuse cookies. If you choose to reject cookies, you may still use our Website though your access to some functionality and areas of our Website may be restricted. As the means by which you can refuse cookies through your web browser controls vary from browser-to-browser, you should visit your browser's help menu for more information.</p>
          
          <p>Most web browsers allow some control of most cookies through the browser settings. To find out more about cookies, including how to see what cookies have been set, visit <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">www.aboutcookies.org</a> or <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">www.allaboutcookies.org</a>.</p>
          
          <SubSectionTitle>How to opt out of Google Analytics</SubSectionTitle>
          <p>To opt out of being tracked by Google Analytics across all websites, visit <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a>.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>5. Changes to this Cookie Policy</SectionTitle>
        <Content>
          <p>We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.</p>
          <p>The date at the bottom of this Cookie Policy indicates when it was last updated.</p>
        </Content>
      </Section>
      
      <Section>
        <SectionTitle>6. Contact Us</SectionTitle>
        <Content>
          <p>If you have any questions about our use of cookies or other technologies, please email us at info@uptrendsystems.com.</p>
        </Content>
      </Section>
      
      <LastUpdated>Last Updated: January 4, 2025</LastUpdated>
    </PageContainer>
  );
};

export default CookiesPage; 